import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import AppLayout from '../../components/AppLayout';
import PageHeader from '../../components/PageHeader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import DataTable from '../../components/DataTable';
import useTableList from '../../utils/useTableList';
import ConfirmDialog from '../../components/ConfirmDialog';
import Drawer from '../../components/Drawer';
import useEditRoute from '../../utils/useEditRoute';
import { MODULE_OPTIONS } from '../../constants/listOptions';
import usePageTitle from '../../utils/usePageTitle';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';

function UserSettings() {
    usePageTitle('Gerenciar Usuários');
    const { toast, toastApiError } = useToast();
    const { can } = useAuth();
    const canUsers = can('User');
    const [roles, setRoles] = useState([]);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    // Form state
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        password_confirmation: '',
        role_id: null,
    });
    const [formLoading, setFormLoading] = useState(false);

    // Carrega dados iniciais
    useEffect(() => {
        loadRoles();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadRoles = async () => {
        try {
            const response = await api.get('/roles');
            setRoles(response.data || []);
        } catch (error) {
            console.error('Error loading roles:', error);
            toastApiError(error, { title: 'Erro ao carregar perfis', fallback: 'Não foi possível carregar perfis' });
        }
    };

    const list = useTableList({
        storageKey: 'users',
        fetcher: (params) => api.get('/users', { params }),
        errorMessage: 'Não foi possível carregar usuários',
    });

    const openDrawerForNew = () => {
        setEditingUser(null);
        setFormData({
            name: '',
            email: '',
            password: '',
            password_confirmation: '',
            role_id: null,
        });
        setDrawerOpen(true);
    };

    const openDrawerForEdit = async (user) => {
        setEditingUser(user);
        try {
            const response = await api.get(`/users/${user.id}`);
            const data = response.data;
            setFormData({
                name: data.name || '',
                email: data.email || '',
                password: '',
                password_confirmation: '',
                role_id: data.role_id ?? null,
            });
            setDrawerOpen(true);
        } catch (error) {
            console.error('Error loading user:', error);
            toast({
                title: 'Erro ao carregar',
                description: 'Não foi possível carregar o usuário',
                variant: 'error',
            });
        }
    };

    const handleDeleteClick = (user) => {
        setDeleteDialog({ open: true, id: user.id });
    };

    const handleDeleteConfirm = async () => {
        try {
            await api.delete(`/users/${deleteDialog.id}`);
            setDeleteDialog({ open: false, id: null });
            list.reload();
            toast({
                title: 'Usuário excluído',
                description: 'O usuário foi excluído com sucesso',
                variant: 'success',
            });
        } catch (error) {
            console.error('Error deleting user:', error);
            toastApiError(error, { title: 'Erro ao excluir', fallback: 'Não foi possível excluir o usuário' });
        }
    };

    const handleFormChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setFormLoading(true);

        try {
            const payload = { ...formData };

            // Remove password fields se estiver vazio (edição)
            if (editingUser && !payload.password) {
                delete payload.password;
                delete payload.password_confirmation;
            }

            // Valida confirmação de senha
            if (payload.password && payload.password !== payload.password_confirmation) {
                toast({
                    title: 'Senha inválida',
                    description: 'As senhas não coincidem',
                    variant: 'error',
                });
                setFormLoading(false);
                return;
            }

            if (editingUser) {
                await api.put(`/users/${editingUser.id}`, payload);
            } else {
                await api.post('/users', payload);
            }
            list.reload();
            toast({
                title: editingUser ? 'Usuário salvo' : 'Usuário criado',
                description: editingUser ? 'Alterações salvas com sucesso' : 'Cadastro realizado com sucesso',
                variant: 'success',
            });
        } catch (error) {
            console.error('Error saving user:', error);
            toastApiError(error, { title: 'Erro ao salvar', fallback: 'Não foi possível salvar o usuário' });
        } finally {
            setFormLoading(false);
        }
    };

    const getRoleBadge = (role) => {
        if (!role) {
            return <Badge variant="destructive">Administrador</Badge>;
        }
        return <Badge variant="secondary">{role.name}</Badge>;
    };

    const columns = [
        {
            key: 'name',
            label: 'Nome',
            className: 'font-medium',
        },
        {
            key: 'email',
            label: 'E-mail',
        },
        {
            key: 'role',
            label: 'Perfil',
            render: (_, row) => getRoleBadge(row.role),
        },
    ];

    const filters = [
        { key: 'name', label: 'Nome', type: 'text' },
        { key: 'email', label: 'E-mail', type: 'text' },
        { key: 'role_id', label: 'Perfil', type: 'select', options: roles.map((r) => ({ value: r.id, label: r.name })) },
        { key: 'module', label: 'Módulo com acesso', type: 'select', options: MODULE_OPTIONS },
    ];

    useEditRoute({
        basePath: '/settings/user',
        ready: list.loaded,
        drawerOpen: drawerOpen,
        open: id => openDrawerForEdit({ id }),
    });

    return (
        <AppLayout>
            <div className="space-y-6">
                <PageHeader
                    title="Usuários"
                    breadcrumbs={[
                        { label: 'Dashboard', path: '/dashboard' },
                        { label: 'Configurações' },
                    ]}
                    actions={[
                        ...(canUsers ? [{
                            label: 'Novo Usuário',
                            icon: 'new',
                            onClick: openDrawerForNew,
                        }] : []),
                    ]}
                />

                {/* Table */}
                <DataTable
                    list={list}
                    filters={filters}
                    searchPlaceholder="Buscar por nome ou e-mail..."
                    columns={columns}
                    editTo={canUsers ? (row) => `/settings/user/${row.id}` : undefined}
                    onEdit={canUsers ? openDrawerForEdit : undefined}
                    onDelete={canUsers ? handleDeleteClick : undefined}
                    emptyMessage="Nenhum usuário cadastrado"
                />
            </div>

            {/* Drawer de Formulário */}
            <Drawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                title={editingUser ? 'Editar Usuário' : 'Novo Usuário'}
                size="md"
                footer={
                    <div className="flex justify-end gap-3">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setDrawerOpen(false)}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            form="user-form"
                            disabled={formLoading}
                        >
                            {formLoading ? 'Salvando...' : 'Salvar'}
                        </Button>
                    </div>
                }
            >
                <form id="user-form" onSubmit={handleFormSubmit} className="space-y-8" autoComplete="off">
                    {/* Seção: Identificação */}
                    <div>
                        <div className="flex items-center gap-2 mb-4">
                            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                            </div>
                            <h3 className="text-md font-semibold text-gray-900">
                                Identificação
                            </h3>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Nome Completo *
                                </label>
                                <Input
                                    name="name"
                                    value={formData.name}
                                    onChange={handleFormChange}
                                    placeholder="Nome do usuário"
                                    required
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        E-mail *
                                    </label>
                                    <Input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleFormChange}
                                        placeholder="usuario@email.com"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Perfil *
                                    </label>
                                    <select
                                        name="role_id"
                                        value={formData.role_id ?? ''}
                                        onChange={handleFormChange}
                                        className="flex h-10 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    >
                                        <option value="">Administrador</option>
                                        {roles.map((r) => (
                                            <option key={r.id} value={r.id}>{r.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Seção: Segurança */}
                    <div>
                        <div className="flex items-center gap-2 mb-4">
                            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                            </div>
                            <h3 className="text-md font-semibold text-gray-900">
                                {editingUser ? 'Alterar Senha' : 'Senha de Acesso'}
                            </h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Senha {!editingUser && '*'}
                                </label>
                                <Input
                                    type="password"
                                    name="password"
                                    value={formData.password}
                                    onChange={handleFormChange}
                                    placeholder="••••••••"
                                    autoComplete="new-password"
                                    required={!editingUser}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Confirmar Senha {!editingUser && '*'}
                                </label>
                                <Input
                                    type="password"
                                    name="password_confirmation"
                                    value={formData.password_confirmation}
                                    onChange={handleFormChange}
                                    placeholder="••••••••"
                                    autoComplete="new-password"
                                    required={!editingUser}
                                />
                            </div>
                        </div>
                    </div>
                </form>
            </Drawer>

            {/* Confirm Dialog */}
            <ConfirmDialog
                open={deleteDialog.open}
                title="Excluir Usuário"
                message="Tem certeza que deseja excluir este usuário? Esta ação não pode ser desfeita."
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDeleteDialog({ open: false, id: null })}
            />
        </AppLayout>
    );
}

export default UserSettings;

